import { z } from "zod";

export const idSchema = z.string().min(1).max(36);

/** BCP 47 language tag (simplified validation: language[-Script][-REGION]). */
export const languageTagSchema = z
  .string()
  .regex(/^[a-z]{2,3}(-[A-Z][a-z]{3})?(-([A-Z]{2}|[0-9]{3}))?$/, "Invalid BCP 47 language tag");

export const currencySchema = z.string().regex(/^[A-Z]{3}$/, "ISO 4217 currency code expected");

export const slugSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Lowercase letters, numbers and dashes only");

export const hexColorSchema = z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, "Hex color expected");

/** Integer amount in currency minor units (e.g. cents). */
export const minorAmountSchema = z.number().int().min(0).max(100_000_000);

export type ApiError = {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
};

export const ERROR_CODES = {
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
  NOT_FOUND: "not_found",
  VALIDATION: "validation_error",
  CONFLICT: "conflict",
  PLAN_LIMIT: "plan_limit",
  RATE_LIMITED: "rate_limited",
  INSUFFICIENT_CREDITS: "insufficient_credits",
  REVIEW_REQUIRED: "review_required",
  INTERNAL: "internal_error",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export function slugify(input: string): string {
  return input
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64) || "menu";
}

/** Common language list offered in the UI. Any valid BCP 47 tag is accepted by the API. */
export const COMMON_LANGUAGES: Array<{ tag: string; name: string; nativeName: string; rtl?: boolean }> = [
  { tag: "en", name: "English", nativeName: "English" },
  { tag: "de", name: "German", nativeName: "Deutsch" },
  { tag: "fr", name: "French", nativeName: "Français" },
  { tag: "it", name: "Italian", nativeName: "Italiano" },
  { tag: "es", name: "Spanish", nativeName: "Español" },
  { tag: "pt", name: "Portuguese", nativeName: "Português" },
  { tag: "nl", name: "Dutch", nativeName: "Nederlands" },
  { tag: "bs", name: "Bosnian", nativeName: "Bosanski" },
  { tag: "hr", name: "Croatian", nativeName: "Hrvatski" },
  { tag: "sr-Latn", name: "Serbian (Latin)", nativeName: "Srpski" },
  { tag: "sr-Cyrl", name: "Serbian (Cyrillic)", nativeName: "Српски" },
  { tag: "sl", name: "Slovenian", nativeName: "Slovenščina" },
  { tag: "pl", name: "Polish", nativeName: "Polski" },
  { tag: "cs", name: "Czech", nativeName: "Čeština" },
  { tag: "tr", name: "Turkish", nativeName: "Türkçe" },
  { tag: "el", name: "Greek", nativeName: "Ελληνικά" },
  { tag: "ru", name: "Russian", nativeName: "Русский" },
  { tag: "uk", name: "Ukrainian", nativeName: "Українська" },
  { tag: "ar", name: "Arabic", nativeName: "العربية", rtl: true },
  { tag: "he", name: "Hebrew", nativeName: "עברית", rtl: true },
  { tag: "zh-Hans", name: "Chinese (Simplified)", nativeName: "简体中文" },
  { tag: "ja", name: "Japanese", nativeName: "日本語" },
  { tag: "ko", name: "Korean", nativeName: "한국어" },
];

const RTL_LANGS = new Set(["ar", "he", "fa", "ur", "ps", "yi", "dv", "ku"]);
export function isRtlLanguage(tag: string): boolean {
  return RTL_LANGS.has(tag.split("-")[0]!.toLowerCase());
}

export function languageName(tag: string): string {
  return COMMON_LANGUAGES.find((l) => l.tag === tag)?.nativeName ?? tag;
}
